import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader2 } from 'lucide-react';
import { Layout } from './Home';
import PageHero from '../components/PageHero';
import RichContent from '../components/RichContent';
import { getCompanyNews, resolveFileUrl } from '../services/api';

const formatDate = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const NewsroomDetail = () => {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    getCompanyNews(slug)
      .then((n) => setItem(n))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (notFound) return <Navigate to="/newsroom" replace />;

  return (
    <Layout>
      {loading || !item ? (
        <div className="bg-[#e2ede6] py-32 text-center text-[#0b1a2e]/60">
          <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…
        </div>
      ) : (
        <>
          <PageHero
            eyebrow={`Press · ${formatDate(item.published_at)}`}
            title={item.title}
            description={item.short_desc}
            image={
              resolveFileUrl(item.image_url) ||
              'https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=1600&q=80'
            }
          />
          <section className="bg-[#e2ede6] py-16 lg:py-24" data-testid="newsroom-detail-page">
            <div className="max-w-4xl mx-auto px-6 lg:px-8">
              <div className="bg-white rounded-2xl border border-black/5 p-6 lg:p-10 shadow-sm">
                <RichContent text={item.long_desc} />

                {item.external_mentions?.length > 0 && (
                  <div className="mt-10 pt-8 border-t border-black/5">
                    <h4
                      className="text-xl text-[#0b1a2e] mb-4"
                      style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
                    >
                      In the media
                    </h4>
                    <ul className="space-y-3">
                      {item.external_mentions.map((m, i) => (
                        <li key={m.url ? `${m.url}-${i}` : `mention-${i}`}>
                          <a
                            href={m.url}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="newsroom-external-mention"
                            className="group flex items-start gap-3 p-3 rounded-lg border border-[#0f5c3d]/15 hover:bg-[#f7fbf8] transition"
                          >
                            <ExternalLink className="w-4 h-4 text-[#0f5c3d] mt-1 shrink-0" />
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-[#0b1a2e] group-hover:text-[#0f5c3d] transition">
                                {m.title}
                              </div>
                              {m.source && (
                                <div className="text-xs text-[#0b1a2e]/60 mt-0.5">
                                  {m.source}
                                </div>
                              )}
                            </div>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="mt-8 text-center">
                <Link
                  to="/newsroom"
                  data-testid="newsroom-back-link"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#0f5c3d]/25 text-[#0b1a2e] text-sm hover:bg-white transition"
                >
                  <ArrowLeft className="w-4 h-4" /> All press releases
                </Link>
              </div>
            </div>
          </section>
        </>
      )}
    </Layout>
  );
};

export default NewsroomDetail;
